import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Segmented, Toggle } from '../components/Controls';
import { PieceIcon } from '../components/Pieces';
import { downloadRecoveryCode, login, recover, register } from '../lib/auth';

const offlineMsg = 'You are offline. Accounts need a connection; local play, the bot and puzzles work without one.';

/** Only same-site paths are allowed as a destination after logging in. */
function useNext(): string {
  const [q] = useSearchParams();
  const next = q.get('next') ?? '';
  return next.startsWith('/') && !next.startsWith('//') ? next : '/profile';
}

function AuthShell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="page auth-page">
      <div className="auth-card card">
        <div className="auth-mark"><PieceIcon icon="elephant" color="w" set="classic" /></div>
        <h1>{title}</h1>
        {subtitle && <p className="muted auth-sub">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function PasswordInput({ value, onChange, autoComplete }: { value: string; onChange: (v: string) => void; autoComplete: string }) {
  const [show, setShow] = useState(false);
  return (
    <span className="password-field">
      <input type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} required minLength={autoComplete === 'new-password' ? 8 : 1} />
      <button type="button" className="link-btn" onClick={() => setShow(!show)}>{show ? 'Hide' : 'Show'}</button>
    </span>
  );
}

/** Shows a fresh recovery code once and offers it as a .txt download. */
export function RecoveryCodeCard({ username, code, onDone, doneLabel = 'Continue' }: { username: string; code: string; onDone: () => void; doneLabel?: string }) {
  const [saved, setSaved] = useState(false);
  return (
    <div className="stack recovery-card">
      <p className="small">This is your recovery code. With it you can choose a new password if you forget yours. It is shown only now.</p>
      <code className="recovery-code">{code}</code>
      <button type="button" className="btn primary block" onClick={() => { downloadRecoveryCode(username, code); setSaved(true); }}>
        Download as .txt
      </button>
      <button type="button" className={`btn ${saved ? 'secondary' : 'ghost'} block`} onClick={onDone}>
        {saved ? doneLabel : `I saved it, ${doneLabel.toLowerCase()}`}
      </button>
    </div>
  );
}

export function Login() {
  const nav = useNavigate();
  const [q] = useSearchParams();
  const next = useNext();
  const [mode, setMode] = useState<'login' | 'signup'>(q.get('mode') === 'signup' ? 'signup' : 'login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [withRecovery, setWithRecovery] = useState(true);
  const [code, setCode] = useState<{ username: string; code: string } | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      if (mode === 'login') { await login(username.trim(), password); nav(next); return; }
      const r = await register(username.trim(), password, withRecovery);
      if (r.recoveryCode) setCode({ username: r.user.name, code: r.recoveryCode });
      else nav(next);
    } catch (x) {
      setErr(navigator.onLine ? (x as Error).message : offlineMsg);
    } finally {
      setBusy(false);
    }
  }

  if (code) {
    return (
      <AuthShell title="Account created" subtitle={`Welcome, ${code.username}!`}>
        <RecoveryCodeCard username={code.username} code={code.code} onDone={() => nav(next)} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title={mode === 'login' ? 'Welcome back' : 'Create your account'} subtitle="Ratings, game history and friends.">
      <Segmented value={mode} onChange={(m) => { setMode(m); setErr(''); }}
        options={[{ value: 'login', label: 'Log in' }, { value: 'signup', label: 'Sign up' }]} />
      <form onSubmit={submit} className="stack auth-form">
        <label>Username {mode === 'signup' && <span className="muted small">shown to other players</span>}
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required autoFocus
            minLength={3} maxLength={20} pattern="[A-Za-z0-9_\-]+" title="3-20 letters, digits, _ or -" />
        </label>
        <label>
          <span className="row" style={{ justifyContent: 'space-between' }}>
            Password
            {mode === 'login' && <Link to="/forgot" className="small">Forgot password?</Link>}
          </span>
          <PasswordInput value={password} onChange={setPassword} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
          {mode === 'signup' && <span className="muted small">At least 8 characters.</span>}
        </label>
        {mode === 'signup' && (
          <Toggle checked={withRecovery} onChange={setWithRecovery} label="Recovery code"
            hint="Optional. Downloads a .txt file with a code that lets you set a new password if you forget it. Without it, a forgotten password cannot be recovered." />
        )}
        {err && <div className="error small">{err}</div>}
        <button className="btn primary block xl" disabled={busy}>{busy ? 'One moment…' : mode === 'login' ? 'Log in' : 'Create account'}</button>
      </form>
      <p className="muted small auth-foot">No account needed to play offline or online as a guest.</p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const nav = useNavigate();
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [fresh, setFresh] = useState<{ username: string; code: string } | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = await recover(username.trim(), code, password);
      setFresh({ username: r.user.name, code: r.recoveryCode! });
    } catch (x) {
      setErr(navigator.onLine ? (x as Error).message : offlineMsg);
    } finally {
      setBusy(false);
    }
  }

  if (fresh) {
    return (
      <AuthShell title="Password changed" subtitle="You are logged in. Your old recovery code no longer works, here is a new one.">
        <RecoveryCodeCard username={fresh.username} code={fresh.code} onDone={() => nav('/profile')} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Forgot your password?" subtitle="Use the recovery code from the .txt file you downloaded when you signed up.">
      <form onSubmit={submit} className="stack auth-form">
        <label>Username<input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required autoFocus /></label>
        <label>Recovery code<input value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" autoComplete="off" required spellCheck={false} /></label>
        <label>New password
          <PasswordInput value={password} onChange={setPassword} autoComplete="new-password" />
          <span className="muted small">At least 8 characters. Other devices get signed out.</span>
        </label>
        {err && <div className="error small">{err}</div>}
        <button className="btn primary block xl" disabled={busy}>Set new password</button>
        <p className="muted small">No recovery code? Then the password cannot be reset; create a new account.</p>
        <Link to="/login" className="small">Back to log in</Link>
      </form>
    </AuthShell>
  );
}
